import { useCallback, useEffect, useState } from 'react';
import { buildAssignedTicketsParams, selectHomeTickets } from '../homeUtils';
import type { Ticket } from '../homeTypes';

const TICKETS_REFRESH_INTERVAL = 300000; // 5 minutes

export function useHomeTickets(
  backendUrl: string,
  email?: string,
): {
  tickets: Ticket[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!email) {
      setTickets([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = buildAssignedTicketsParams(email);
      const response = await fetch(`${backendUrl}/api/jira/search?${params}`);
      if (!response.ok)
        throw new Error(`Jira request failed: ${response.status}`);
      const data: { issues?: Ticket[] } = await response.json();
      // Sort tickets: In Progress first, then others
      setTickets(selectHomeTickets(data.issues ?? []));
    } catch (error) {
      console.error('Error fetching assigned tickets:', error);
      setTickets([]);
      setError('Los tickets asignados no están disponibles.');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, email]);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(refresh, TICKETS_REFRESH_INTERVAL);
    window.addEventListener('focus', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
    };
  }, [refresh]);

  return { tickets, loading, error, refresh };
}
